import { useEffect, useState } from "react";
// import { useAuth } from "../../../../Providers/Auth";
import { useToast } from "../../../../shadcnComponents/ui/use-toast";
import { LoginPopup } from "../../CreatorLandingPage/CreatorLandingPage";
import { GradientDivider } from "../../LocationChapters/Components/atoms";
import { Footer } from "../../LocationChapters/Components/organisms";
import { LPEPageBody, LPESection } from "../Components/atoms";
import { LinkedinPostEditorHeader } from "../Components/molecules";
import { Header, LinkedinPostPreviewPopup } from "../Components/organisms";
import { InitialEditorProps, LinkedinPostEditor } from "./LinkedinPostEditor";
const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

export const HomePage = () => {
  const { toast } = useToast();
  const [openLoginPopup, setOpenLoginPopup] = useState(false);
  const [currentEditorState, setCurrentEditorState] =
    useState<InitialEditorProps>(null);
  const [toolbarData, setToolbarData] = useState({
    showTrash: false,
  });
  const [openLinkedinPreviewPopup, setOpenLinkedinPreviewPopup] =
    useState(false);
  const [uniqueString, setUniqueString] = useState();
  const queryPrams = new URLSearchParams(window.location.search);
  const query = queryPrams.get("q");

  useEffect(() => {
    const getSavedData = async () => {
      try {
        const response = await fetch(`${host}/preview/linkedin-post/${query}`);

        // Check if the response is okay
        if (!response.ok) {
          throw new Error(`Failed to fetch draft. Status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          const editorState = {
            ...data.post,
            dummyText: (
              <div>
                <p>
                  <b>
                    Make sure your LinkedIn post hits the mark before it goes
                    live! 📝
                  </b>
                </p>
                <br />
                <p>
                  With our <b>LinkedIn Post Preview Tool</b>, you can:
                </p>
                ✅ <b>Preview your post</b> to keep your key message above the
                ‘see more’ cut-off.
                <br />✅ <b>Share a preview link</b> with your team for
                feedback.
                <br />✅ <b>Make edits easily</b>, with every version getting
                its own shareable link.
                <br />
                <br />
                No guesswork, just clean, clear content.
                <br />
                <b>👉 Give it a try and see the difference!</b>
                <br />
                Let me know if this feels better! 😊
              </div>
            ),
          };

          setCurrentEditorState(editorState);
          setToolbarData({ showTrash: true });
        } else {
          throw new Error("Draft data not found");
        }
      } catch (error) {
        console.error("Error loading draft:", error);

        toast({
          title: "Error loading draft",
          description:
            error instanceof Error ? error.message : "Please try again later",
          variant: "destructive",
        });
      }
    };
    if (query) {
      getSavedData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleSaveDraft = async (editorState: InitialEditorProps) => {
    try {
      // Validate required fields
      if (!editorState.content || editorState.content.trim() === "") {
        return toast({
          title: "Please write content before saving",
          variant: "destructive",
        });
      }
      setCurrentEditorState(editorState);
      const newEditorState = editorState;
      newEditorState.dummyText = "";

      const response = await fetch(`${host}/preview/linkedin-post`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newEditorState),
      });

      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }

      const result = await response.json();

      if (result.uniqueString) {
        setUniqueString(result.uniqueString);
        setOpenLinkedinPreviewPopup(true);
        setToolbarData((p) => ({ ...p, showTrash: true }));
        toast({
          title: "Draft saved successfully",
        });
      } else {
        throw new Error("Unexpected response format");
      }
    } catch (error) {
      console.error("Error saving draft:", error);

      toast({
        title: "Error saving draft",
        description:
          error instanceof Error ? error.message : "Please try again later",
        variant: "destructive",
      });
    }
  };
  return (
    <>
      {openLoginPopup && (
        <LoginPopup onClose={() => setOpenLoginPopup(false)} backToSource />
      )}
      {openLinkedinPreviewPopup && (
        <LinkedinPostPreviewPopup
          isOpen={true}
          onClose={function (): void {
            setOpenLinkedinPreviewPopup(false);
          }}
          link={`${window.location.origin}/linkedin-post-preview/?q=${uniqueString}`}
          shareText={currentEditorState?.content}
        />
      )}
      <div className="flex flex-col min-h-screen bg-[#171717] text-white relative">
        {/* <CustomCursor /> */}
        <Header bg={"bg-[#171717]"} hideName={true} />
        <LPEPageBody>
          <div className="flex flex-col gap-10">
            <LinkedinPostEditorHeader />
            <div className="flex flex-col items-center justify-center">
              <div className="p-2 md:p-5 w-full md:w-11/12">
                <LinkedinPostEditor
                  initial={currentEditorState}
                  onSaveDraft={({ editorState }) =>
                    handleSaveDraft(editorState)
                  }
                  onChange={(editorState) => {
                    setCurrentEditorState(editorState);
                  }}
                  showTrash={toolbarData?.showTrash}
                  clearEditor={() => {
                    setCurrentEditorState((prev) => ({
                      ...prev,
                      content: "",
                    }));
                    localStorage.removeItem("draft");
                    toolbarData?.showTrash &&
                      setToolbarData((p) => ({ ...p, showTrash: false }));
                  }}
                />
              </div>
            </div>
          </div>
          <LPESection title={"Why we built this tool"}>
            <div className="w-11/12">
              <p className="text-sm md:text-base text-[#919191] font-[Inter] not-italic font-normal leading-[28px]">
                We created this LinkedIn post preview tool with a clear purpose:
                to help content creators, founders, marketers, and anyone
                posting on LinkedIn see exactly how their posts will appear in
                the LinkedIn feed. This tool ensures that your most engaging
                content, a.k.a hook line, stays above the 'see more' cut-off,
                optimizing visibility and audience engagement.
                <br />
                <br />
                While many LinkedIn preview tools exist, most fall short on
                accuracy of this cut-off line, making it hard for users to
                optimize their posts.
                <br />
                <br />
                Originally, we built this tool for internal use, giving content
                creators a reliable way to preview posts and align content with
                brand guidelines during collaborations. However, after seeing
                the gaps in other preview tools, we decided to make ours public,
                completely free.
                <br />
                <br />
                Our goal is to provide a LinkedIn preview tool that’s simple,
                accurate, and easy to use. If you encounter any glitches or have
                feedback, please reach out via email. We’re committed to
                continuously improving your experience with the tool.
              </p>
            </div>
          </LPESection>
          <LPESection title={"Empowering Creators at Every Stage"}>
            <div className="w-11/12">
              <p className="text-sm md:text-base text-[#919191] font-[Inter] not-italic font-normal leading-[28px]">
                anchors is designed to empower creators at every stage of their
                journey, giving you the tools, community, and insights to
                succeed in the creator economy. Here’s how we support you:
                <br />
                <ul className="list-disc pl-4">
                  <li>
                    <b>Effortless Brand Collaborations:</b> Get matched with
                    verified brands that align with your values and audience—no
                    negotiations needed. Authentic partnerships simplified.
                  </li>
                  <li>
                    <b>Performance-based Earning:</b> Our unique
                    performance-based earnings model means your income grows
                    with the success of your posts. With our LinkedIn Pricing
                    Calculator, you can also see your potential earnings and set
                    informed rates for each brand collaboration.
                  </li>
                  <li>
                    <b>Connect with Creators in your City:</b> Through “City
                    Chapters”, we bring creators together in their own cities,
                    creating local communities for connection, collaboration,
                    and mutual support. It’s the perfect way to build local
                    connections within the community.
                  </li>
                  <li>
                    <b>Access a Supportive Creator Community:</b> Join a
                    nationwide network of fellow LinkedIn creators, where you
                    can exchange ideas, gain insights, and collaborate on
                    projects for growth and learning.
                  </li>
                  <li>
                    <b>Get Real-Time Insights and Analytics:</b> Understand your
                    audience better with insights that guide your growth,
                    helping you stay on top of trends and increase your reach.
                  </li>
                </ul>
                <br />
                <b>anchors</b> isn’t just another platform – it’s a place where
                creators can grow, connect with others, and reach their goals.
                Join us to find new ways to share your voice, make an impact,
                and earn what you deserve.
              </p>
            </div>
          </LPESection>
          <div className="w-full">
            <GradientDivider className="h-[1px]" />
            <Footer />
          </div>
        </LPEPageBody>
      </div>
    </>
  );
};
